import MapGeocodingApiService from "../../infrastructure/api/mapGeocoding";
import { NeshanMapProvider } from "../../providers/map/NeshanMapProvider";
import type { IMapProvider } from "../../providers/map/IMapProvider";
import {
  mapConfig,
  MapClientConfig,
  normalizeMapClientConfig,
} from "./mapConfig";
import type {
  MapLocation,
  MapSearchOptions,
  MapSearchResult,
  MapTileConfig,
  ReverseGeocodeOptions,
} from "./types";
import { MapServiceError } from "./types";

let providerInstance: IMapProvider | null = null;
let clientConfigPromise: Promise<MapClientConfig | null> | null = null;
let cachedClientConfig: MapClientConfig | null = null;

function getProvider(): IMapProvider {
  if (!providerInstance) {
    providerInstance = new NeshanMapProvider();
  }
  return providerInstance;
}

async function loadClientConfig(force = false): Promise<MapClientConfig | null> {
  if (!force && cachedClientConfig) {
    return cachedClientConfig;
  }

  if (!force && clientConfigPromise) {
    return clientConfigPromise;
  }

  clientConfigPromise = MapGeocodingApiService.getClientConfig()
    .then((response) => {
      if (!response?.isSuccess || !response?.data) {
        cachedClientConfig = null;
        return null;
      }

      cachedClientConfig = normalizeMapClientConfig(response.data);
      return cachedClientConfig;
    })
    .catch(() => {
      cachedClientConfig = null;
      return null;
    })
    .finally(() => {
      clientConfigPromise = null;
    });

  return clientConfigPromise;
}

export const mapService = {
  async ensureReady(force = false): Promise<MapClientConfig | null> {
    return loadClientConfig(force);
  },

  isConfigured(): boolean {
    return Boolean(cachedClientConfig?.isConfigured && cachedClientConfig?.apiKey);
  },

  async getApiKey(): Promise<string> {
    const config = await loadClientConfig();
    return config?.apiKey?.trim() ?? "";
  },

  getDefaultCenter(): [number, number] {
    return cachedClientConfig?.defaultCenter ?? mapConfig.defaultCenter;
  },

  getDefaultZoom(): number {
    return cachedClientConfig?.defaultZoom ?? mapConfig.defaultZoom;
  },

  getPickerZoom(): number {
    return mapConfig.pickerZoom;
  },

  getSearchDebounceMs(): number {
    return mapConfig.searchDebounceMs;
  },

  async getTileConfig(): Promise<MapTileConfig> {
    const config = await loadClientConfig();
    return {
      provider: "neshan",
      mapType: config?.mapType ?? mapConfig.defaultMapType,
      apiKey: config?.apiKey ?? "",
    };
  },

  search(query: string, options?: MapSearchOptions): Promise<MapSearchResult[]> {
    return getProvider().search(query, options);
  },

  reverseGeocode(
    lat: number,
    lng: number,
    options?: ReverseGeocodeOptions
  ): Promise<MapLocation> {
    return getProvider().reverseGeocode(lat, lng, options);
  },

  invalidateClientConfig(): void {
    cachedClientConfig = null;
    clientConfigPromise = null;
  },
};

export { MapServiceError };
export type {
  MapLocation,
  MapSearchResult,
  MapTileConfig,
  MapSearchOptions,
  ReverseGeocodeOptions,
  MapClientConfig,
};
